import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronDown } from "lucide-react";
import {
  Timeframe,
  useAnalyticsUniverse,
  useCorrelationMatrix,
} from "@/hooks/useCorrelationMatrix";

function corrColor(v: number): string {
  // v in [-1, 1] -> diverging red / grey / green
  if (!isFinite(v)) return "hsl(0 0% 12%)";
  const clamped = Math.max(-1, Math.min(1, v));
  const abs = Math.abs(clamped);
  if (clamped >= 0) {
    // dark grey -> deep green (#16A34A)
    // lerp lightness/opacity via rgba
    const alpha = 0.15 + abs * 0.75;
    return `rgba(22, 163, 74, ${alpha.toFixed(3)})`;
  }
  const alpha = 0.15 + abs * 0.75;
  return `rgba(220, 38, 38, ${alpha.toFixed(3)})`;
}

function fmt(v: number): string {
  if (!isFinite(v)) return "—";
  return (v >= 0 ? " " : "") + v.toFixed(2);
}

export function CorrelationHeatmap() {
  const [timeframe, setTimeframe] = useState<Timeframe>("30d");
  const [showGuide, setShowGuide] = useState(false);
  const { data: universe, isLoading: uLoading } = useAnalyticsUniverse();
  const { data: rows, isLoading: rLoading } = useCorrelationMatrix(timeframe);

  const loading = uLoading || rLoading;
  const symbols = useMemo(() => (universe ?? []).map((c) => c.symbol), [universe]);

  const lookup = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows ?? []) {
      m.set(`${r.coin_a_symbol}|${r.coin_b_symbol}`, r.correlation);
      m.set(`${r.coin_b_symbol}|${r.coin_a_symbol}`, r.correlation);
    }
    return m;
  }, [rows]);

  const insights = useMemo(() => {
    if (!rows || rows.length === 0) return null;
    let hi = rows[0];
    let lo = rows[0];
    let sum = 0;
    for (const r of rows) {
      if (r.correlation > hi.correlation) hi = r;
      if (r.correlation < lo.correlation) lo = r;
      sum += r.correlation;
    }
    return { hi, lo, avg: sum / rows.length };
  }, [rows]);

  const lastComputed = useMemo(() => {
    if (!rows || rows.length === 0) return null;
    return rows.reduce((m, r) => (r.computed_at > m ? r.computed_at : m), rows[0].computed_at);
  }, [rows]);

  const stale =
    lastComputed && Date.now() - new Date(lastComputed).getTime() > 48 * 60 * 60 * 1000;

  return (
    <TooltipProvider delayDuration={100}>
      <div className="space-y-6">
        {/* Timeframe selector */}
        <div className="flex items-center gap-2">
          {(["30d", "60d", "90d"] as Timeframe[]).map((tf) => (
            <button
              key={tf}
              onClick={() => setTimeframe(tf)}
              className={`px-4 py-1.5 text-xs font-mono uppercase tracking-wider rounded-full border transition-colors ${
                timeframe === tf
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border/60 text-muted-foreground hover:text-foreground hover:border-border"
              }`}
            >
              {tf.toUpperCase()}
            </button>
          ))}
          {stale && (
            <Badge variant="outline" className="ml-auto border-warning/40 text-warning">
              Data older than 48h
            </Badge>
          )}
        </div>

        {/* Matrix */}
        {loading ? (
          <div className="grid gap-px" style={{ gridTemplateColumns: `repeat(${(symbols.length || 10) + 1}, minmax(56px, 1fr))` }}>
            {Array.from({ length: ((symbols.length || 10) + 1) ** 2 }).map((_, i) => (
              <Skeleton key={i} className="h-12 rounded-none" />
            ))}
          </div>
        ) : !rows || rows.length === 0 ? (
          <div className="border border-border/50 bg-card/30 p-8 text-center text-sm text-muted-foreground">
            Correlation data is being computed and will be available shortly. Data refreshes daily at
            6:00 AM AEST.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div
              className="inline-grid gap-px bg-border/40 min-w-full"
              style={{ gridTemplateColumns: `72px repeat(${symbols.length}, minmax(64px, 1fr))` }}
            >
              {/* Header row */}
              <div className="bg-background sticky left-0 z-10" />
              {symbols.map((s) => (
                <div
                  key={`h-${s}`}
                  className="bg-background px-2 py-2 text-center text-[11px] font-mono font-semibold text-muted-foreground uppercase"
                >
                  {s}
                </div>
              ))}
              {symbols.map((rowSym) => (
                <div key={`r-${rowSym}`} className="contents">
                  <div className="bg-background sticky left-0 z-10 px-2 py-2 text-right text-[11px] font-mono font-semibold text-muted-foreground uppercase border-r border-border/40">
                    {rowSym}
                  </div>
                  {symbols.map((colSym) => {
                    if (rowSym === colSym) {
                      return (
                        <div
                          key={`${rowSym}-${colSym}`}
                          className="bg-muted/30 px-1 py-2 text-center font-mono text-[11px] text-muted-foreground"
                        >
                          1.00
                        </div>
                      );
                    }
                    const v = lookup.get(`${rowSym}|${colSym}`);
                    const val = typeof v === "number" ? v : NaN;
                    const pct = Math.round(Math.abs(isFinite(val) ? val : 0) * 100);
                    const direction = val >= 0 ? "same" : "opposite";
                    return (
                      <Tooltip key={`${rowSym}-${colSym}`}>
                        <TooltipTrigger asChild>
                          <div
                            className="px-1 py-2 text-center font-mono text-[11px] text-foreground cursor-default"
                            style={{ backgroundColor: corrColor(val) }}
                          >
                            {fmt(val)}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="font-mono text-xs">
                          {rowSym} ↔ {colSym}: {fmt(val)} correlation ({timeframe.toUpperCase()}) —
                          moved in the {direction} direction {pct}% of the time
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Insights */}
        {insights && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border/40 border border-border/40">
            <div className="bg-card p-4">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                Highest correlation
              </div>
              <div className="font-mono text-lg font-semibold">
                {insights.hi.coin_a_symbol} ↔ {insights.hi.coin_b_symbol}
              </div>
              <div className="font-mono text-sm text-success">
                {fmt(insights.hi.correlation)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Strongest co-movement in the current period
              </div>
            </div>
            <div className="bg-card p-4">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                Lowest correlation
              </div>
              <div className="font-mono text-lg font-semibold">
                {insights.lo.coin_a_symbol} ↔ {insights.lo.coin_b_symbol}
              </div>
              <div
                className={`font-mono text-sm ${
                  insights.lo.correlation < 0 ? "text-destructive" : "text-foreground"
                }`}
              >
                {fmt(insights.lo.correlation)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Most independent price action
              </div>
            </div>
            <div className="bg-card p-4">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                Average correlation
              </div>
              <div className="font-mono text-lg font-semibold">{fmt(insights.avg)}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {insights.avg > 0.8
                  ? "Highly correlated — market moving in lockstep"
                  : insights.avg > 0.5
                  ? "Moderately correlated, typical of a trending environment"
                  : "Low correlation — meaningful diversification available"}
              </div>
            </div>
          </div>
        )}

        {/* Last updated */}
        {lastComputed && (
          <div className="text-[11px] font-mono text-muted-foreground">
            Last computed:{" "}
            {new Date(lastComputed).toLocaleString("en-AU", {
              timeZone: "Australia/Sydney",
              dateStyle: "medium",
              timeStyle: "short",
            })}{" "}
            AEST
          </div>
        )}

        {/* Interpretation guide */}
        <div className="border border-border/40 bg-card/30">
          <button
            onClick={() => setShowGuide((s) => !s)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold"
          >
            <span>How to read this</span>
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showGuide ? "rotate-180" : ""}`}
            />
          </button>
          {showGuide && (
            <div className="px-4 pb-4 space-y-2 text-sm text-muted-foreground">
              <div>
                <span className="font-mono text-success">0.8 to 1.0</span> — Very high correlation.
                Coins move almost in lockstep; little diversification benefit.
              </div>
              <div>
                <span className="font-mono">0.5 to 0.8</span> — Moderate correlation. Shared
                movement with meaningful independence.
              </div>
              <div>
                <span className="font-mono">0.0 to 0.5</span> — Low correlation. Largely
                independent; good diversification candidates.
              </div>
              <div>
                <span className="font-mono text-destructive/80">-0.5 to 0.0</span> — Weak negative
                correlation. Occasional inverse movement.
              </div>
              <div>
                <span className="font-mono text-destructive">-1.0 to -0.5</span> — Strong negative
                correlation. Tend to move in opposite directions.
              </div>
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
